import { useTranslation } from "react-i18next";
import type { ChangeSection as ChangeSectionType } from "../../lib/changelogParser";

const SECTION_COLORS: Record<ChangeSectionType["type"], string> = {
  Added: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  Changed: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  Fixed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  Removed: "bg-red-500/20 text-red-400 border-red-500/30",
  Deprecated: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  Security: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  Performance: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
};

interface ChangeSectionProps {
  section: ChangeSectionType;
}

/** Render inline markdown: **bold**, `code`, and [links](url) */
function renderInlineMarkdown(text: string) {
  // Split on all inline markdown tokens in one pass
  const tokenRegex = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(tokenRegex);

  return parts.map((part, index) => {
    // Bold
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={index} className="text-warm-100 font-semibold">
          {boldMatch[1]}
        </strong>
      );
    }

    // Inline code
    const codeMatch = part.match(/^`(.+)`$/);
    if (codeMatch) {
      return (
        <code
          key={index}
          className="text-xs bg-dark-bg text-pink-400 px-1.5 py-0.5 rounded font-mono"
        >
          {codeMatch[1]}
        </code>
      );
    }

    // Link
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={index}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          {linkMatch[1]}
        </a>
      );
    }

    return part;
  });
}

export function ChangeSection({ section }: ChangeSectionProps) {
  const { t } = useTranslation();
  const colorClasses = SECTION_COLORS[section.type];

  return (
    <div className="mb-4">
      <div
        className={`inline-block px-2 py-0.5 rounded text-xs font-medium border mb-2 ${colorClasses}`}
      >
        {t(`changelog.sections.${section.type}`)}
      </div>
      <ul className="space-y-1.5">
        {section.entries.map((entry, index) => (
          <li key={index} className="flex gap-2 text-warm-200 text-sm leading-relaxed">
            <span className="text-warm-500 mt-0.5 select-none">-</span>
            <span>{renderInlineMarkdown(entry.text)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
